import * as THREE from 'three';
import { Spell, SpellConfig } from './Spell';

export class Fireball extends Spell {
  private core!: THREE.Mesh;
  private glow!: THREE.Mesh;
  private trail!: THREE.Points;
  private trailPositions!: Float32Array;
  private impactAnimationId: number | null = null;

  constructor(scene: THREE.Scene, startPos: THREE.Vector3, targetPos: THREE.Vector3, damage: number, isPlayerSpell: boolean = true) {
    const config: SpellConfig = {
      type: 'fireball',
      damage,
      speed: 15,
      color: new THREE.Color(0xFF6B35),
      particleCount: 50,
      chargeTime: 1 // 1 second charge time for reaction
    };
    super(scene, startPos, targetPos, config, isPlayerSpell);
  }

  protected createSpellVisual(): void {
    const coreGeometry = new THREE.SphereGeometry(0.3, 16, 16);
    const coreMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFFF00,
      transparent: true,
      opacity: 1
    });
    this.core = new THREE.Mesh(coreGeometry, coreMaterial);
    this.mesh.add(this.core);

    const glowGeometry = new THREE.SphereGeometry(0.5, 16, 16);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xFF6B35,
      transparent: true,
      opacity: 0.6
    });
    this.glow = new THREE.Mesh(glowGeometry, glowMaterial);
    this.mesh.add(this.glow);

    const outerGlowGeometry = new THREE.SphereGeometry(0.7, 16, 16);
    const outerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0xFF4500,
      transparent: true,
      opacity: 0.3
    });
    const outerGlow = new THREE.Mesh(outerGlowGeometry, outerGlowMaterial);
    this.mesh.add(outerGlow);

    this.createTrail();
  }

  private createTrail(): void {
    const trailCount = 30;
    this.trailPositions = new Float32Array(trailCount * 3);

    for (let i = 0; i < trailCount * 3; i++) {
      this.trailPositions[i] = this.mesh.position.x + (i % 3 === 0 ? 0 : (i % 3 === 1 ? 0 : 0));
    }

    const trailGeometry = new THREE.BufferGeometry();
    trailGeometry.setAttribute('position', new THREE.BufferAttribute(this.trailPositions, 3));

    const trailMaterial = new THREE.PointsMaterial({
      color: 0xFF6B35,
      size: 0.2,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    });

    this.trail = new THREE.Points(trailGeometry, trailMaterial);
    this.scene.add(this.trail);
  }

  protected createParticles(): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.config.particleCount * 3);
    const colors = new Float32Array(this.config.particleCount * 3);

    for (let i = 0; i < this.config.particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const r = 0.3 + Math.random() * 0.3;

      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      colors[i * 3] = 1;
      colors[i * 3 + 1] = 0.3 + Math.random() * 0.4;
      colors[i * 3 + 2] = 0;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.1,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    });

    const particles = new THREE.Points(geometry, material);
    this.mesh.add(particles);
    return particles;
  }

  protected updateParticles(_delta: number): void {
    if (!this.particles || !this.particles.geometry.attributes.position) return;

    const positions = this.particles.geometry.attributes.position.array as Float32Array;

    for (let i = 0; i < this.config.particleCount; i++) {
      positions[i * 3] += (Math.random() - 0.5) * 0.1;
      positions[i * 3 + 1] += (Math.random() - 0.5) * 0.1;
      positions[i * 3 + 2] += (Math.random() - 0.5) * 0.1;

      const dist = Math.sqrt(
        positions[i * 3] ** 2 +
        positions[i * 3 + 1] ** 2 +
        positions[i * 3 + 2] ** 2
      );

      if (dist > 0.6) {
        positions[i * 3] *= 0.5 / dist;
        positions[i * 3 + 1] *= 0.5 / dist;
        positions[i * 3 + 2] *= 0.5 / dist;
      }
    }

    this.particles.geometry.attributes.position.needsUpdate = true;

    if (this.glow) {
      this.glow.scale.setScalar(1 + Math.sin(Date.now() * 0.01) * 0.1);
    }

    if (this.trail && this.trailPositions) {
      const trailCount = this.trailPositions.length / 3;
      for (let i = trailCount - 1; i > 0; i--) {
        this.trailPositions[i * 3] = this.trailPositions[(i - 1) * 3];
        this.trailPositions[i * 3 + 1] = this.trailPositions[(i - 1) * 3 + 1];
        this.trailPositions[i * 3 + 2] = this.trailPositions[(i - 1) * 3 + 2];
      }
      this.trailPositions[0] = this.mesh.position.x;
      this.trailPositions[1] = this.mesh.position.y;
      this.trailPositions[2] = this.mesh.position.z;

      this.trail.geometry.attributes.position.needsUpdate = true;
    }
  }

  public createImpactEffect(): void {
    this.emitEvent('impact', { position: this.impactPosition.toArray(), type: 'fireball' });

    const explosionParticleCount = 150;
    const positions = new Float32Array(explosionParticleCount * 3);
    const colors = new Float32Array(explosionParticleCount * 3);
    const velocities: THREE.Vector3[] = [];
    const impactPos = this.impactPosition;

    for (let i = 0; i < explosionParticleCount; i++) {
      positions[i * 3] = impactPos.x;
      positions[i * 3 + 1] = impactPos.y;
      positions[i * 3 + 2] = impactPos.z;

      // Color gradient: white core -> orange -> dark red edges
      const t = i / explosionParticleCount;
      if (t < 0.2) {
        colors[i * 3] = 1; colors[i * 3 + 1] = 1; colors[i * 3 + 2] = 0.7;
      } else if (t < 0.6) {
        colors[i * 3] = 1; colors[i * 3 + 1] = 0.4 + Math.random() * 0.3; colors[i * 3 + 2] = 0;
      } else {
        colors[i * 3] = 0.8; colors[i * 3 + 1] = 0.1 + Math.random() * 0.15; colors[i * 3 + 2] = 0;
      }

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 4 + Math.random() * 8;
      velocities.push(new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.sin(phi) * Math.sin(theta) * speed * 0.8 + 2,
        Math.cos(phi) * speed
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.25,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending
    });

    const explosion = new THREE.Points(geometry, material);
    this.scene.add(explosion);

    // Add flash light at impact
    const flashLight = new THREE.PointLight(0xFF6B35, 4, 12);
    flashLight.position.copy(impactPos);
    this.scene.add(flashLight);

    // Smoke ring expanding outward
    const smokeRingGeo = new THREE.RingGeometry(0.2, 0.8, 24);
    const smokeRingMat = new THREE.MeshBasicMaterial({
      color: 0xFF4400,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide
    });
    const smokeRing = new THREE.Mesh(smokeRingGeo, smokeRingMat);
    smokeRing.position.copy(impactPos);
    smokeRing.lookAt(this.startPosition);
    this.scene.add(smokeRing);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = explosion.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < explosionParticleCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        velocities[i].y -= 6 * 0.016; // gravity on embers
      }
      explosion.geometry.attributes.position.needsUpdate = true;

      material.opacity = Math.max(0, 1 - (elapsed / 0.7));
      flashLight.intensity = 4 * Math.max(0, 1 - (elapsed / 0.3));
      smokeRing.scale.setScalar(1 + elapsed * 6);
      smokeRingMat.opacity = 0.6 * Math.max(0, 1 - (elapsed / 0.4));

      if (elapsed < 0.7) {
        this.impactAnimationId = requestAnimationFrame(animate);
      } else {
        this.impactAnimationId = null;
        this.scene.remove(explosion);
        this.scene.remove(flashLight);
        this.scene.remove(smokeRing);
        geometry.dispose();
        material.dispose();
        flashLight.dispose();
        smokeRingGeo.dispose();
        smokeRingMat.dispose();
      }
    };

    this.impactAnimationId = requestAnimationFrame(animate);
  }

  dispose(): void {
    if (this.impactAnimationId !== null) {
      cancelAnimationFrame(this.impactAnimationId);
      this.impactAnimationId = null;
    }
    super.dispose();
    if (this.trail) {
      this.scene.remove(this.trail);
      this.trail.geometry.dispose();
      (this.trail.material as THREE.Material).dispose();
    }
  }
}
